/**
 * What a train is made of and how it looks: a locomotive and coaches, a
 * multiple unit of a few cars, or a freight engine and its wagons; each
 * car's length and height, and its livery.
 *
 * In Sweden a live train comes with its product and operator from
 * Trafikverket ("Västtågen", SJ; "SJ Snabbtåg", SJ; a freight train with
 * none): each is dressed as the trains that run them look, roughly. A
 * made-up train elsewhere is one of the same kinds, picked by a seed.
 *
 * Pure: `three/trains.ts` builds the models.
 */

export type CarShape =
  /** A multiple unit's driving car: a sloped nose at its front. */
  | 'cab'
  /** A multiple unit's middle car, or a coach. */
  | 'middle'
  /** An electric or diesel locomotive: a cab at each end, no windows down the side. */
  | 'loco'
  /** A high-speed power car: a long pointed nose. */
  | 'power'
  /** A double-deck car, two rows of windows. */
  | 'double'
  /** A freight wagon: a box van, a tank, or a flat with containers. */
  | 'van' | 'tank' | 'flat';

export interface CarSpec {
  shape: CarShape;
  /** Over the buffers, metres. */
  length: number;
  /** Turned round: a cab or a nose at its back, as the last car of a unit has. */
  reversed?: boolean;
}

export interface Livery {
  /** The body, sRGB hex. */
  body: number;
  /** The band along the windows, or the lower side. */
  band: number;
  /** The front of a cab. */
  nose: number;
}

export type TrainKind = 'commuter' | 'regional' | 'intercity' | 'highspeed' | 'freight';

export interface Consist {
  kind: TrainKind;
  cars: CarSpec[];
  livery: Livery;
  /** Top speed on open line, m/s. */
  top: number;
  /** Electric (a pantograph up) or diesel. */
  electric: boolean;
}

/** A multiple unit of `n` cars: a cab at each end, `middle` between. */
function unit(n: number, length: number, middle: CarShape = 'middle'): CarSpec[] {
  return Array.from({ length: n }, (_, i): CarSpec => (i === 0 ? { shape: 'cab', length }
    : i === n - 1 ? { shape: 'cab', length, reversed: true } : { shape: middle, length }));
}

const LIVERIES: Record<string, Livery> = {
  vasttag: { body: 0xf2f2ee, band: 0x1f5ba8, nose: 0x1f5ba8 },
  pagatag: { body: 0x7a2e8c, band: 0x2b1f33, nose: 0xf0c419 },
  oresund: { body: 0xb9bdc2, band: 0x1d1f22, nose: 0x1d1f22 },
  sj: { body: 0x5b6167, band: 0x1d2023, nose: 0x3a3f44 },
  sjX2: { body: 0xc4c8cc, band: 0x1a1c1f, nose: 0x1a1c1f },
  sl: { body: 0xe9ecef, band: 0x14467a, nose: 0x14467a },
  malartag: { body: 0x5b3f8f, band: 0x1f1a2b, nose: 0x5b3f8f },
  norrtag: { body: 0xe8eaec, band: 0xc1272d, nose: 0xc1272d },
  krosa: { body: 0x2a6f3a, band: 0x14301c, nose: 0x2a6f3a },
  arlanda: { body: 0xf3c419, band: 0x2a2a2a, nose: 0xf3c419 },
  mtrx: { body: 0x1d1d1f, band: 0xb3a26b, nose: 0x1d1d1f },
  snalltaget: { body: 0x3b3f44, band: 0xe2702a, nose: 0xe2702a },
  vy: { body: 0xd8dcd9, band: 0x1f7a4d, nose: 0x1f7a4d },
  plain: { body: 0xd6d8da, band: 0x2a4d78, nose: 0x2a4d78 },
  red: { body: 0xb5322d, band: 0x2a2a2a, nose: 0xb5322d },
  greenCargo: { body: 0x2f7d3b, band: 0x1a1a1a, nose: 0xe9c44a },
};

/**
 * The train Trafikverket calls `product`, run by `operator`, as near as the
 * game can make it. `seed` picks among the kinds where the product says
 * little (a freight train's wagons; how many units).
 */
export function consistOf(product: string, operator: string, seed: number): Consist {
  const p = product.toLowerCase(), o = operator.toLowerCase();
  const pick = (n: number, k = 1) => Math.floor(rand(seed, k) * n);
  if (p.includes('snabbtåg') || p.includes('x 2000') || p.includes('x2000')) {
    return { kind: 'highspeed', electric: true, top: 55, livery: LIVERIES.sjX2,
      cars: [{ shape: 'power', length: 17 }, ...Array.from({ length: 5 }, () => ({ shape: 'middle' as CarShape, length: 25 })), { shape: 'cab', length: 25, reversed: true }] };
  }
  if (p.includes('mtrx') || o.includes('mtr')) return { kind: 'highspeed', electric: true, top: 55, livery: LIVERIES.mtrx, cars: unit(5, 27) };
  if (p.includes('arlanda')) return { kind: 'intercity', electric: true, top: 55, livery: LIVERIES.arlanda, cars: unit(4, 23) };
  if (p.includes('pendeltåg') || p.includes('sl ')) return { kind: 'commuter', electric: true, top: 40, livery: LIVERIES.sl, cars: unit(6, 18) };
  if (p.includes('västtåg')) return { kind: 'regional', electric: true, top: 44, livery: LIVERIES.vasttag, cars: unit(2 + pick(2), 27) };
  if (p.includes('pågatåg')) return { kind: 'regional', electric: true, top: 44, livery: LIVERIES.pagatag, cars: unit(2 + pick(2), 27) };
  if (p.includes('öresund')) return { kind: 'regional', electric: true, top: 50, livery: LIVERIES.oresund, cars: unit(3 + 3 * pick(2), 26) };
  if (p.includes('mälartåg') || p.includes('upptåg')) return { kind: 'regional', electric: true, top: 55, livery: LIVERIES.malartag, cars: unit(4, 26, 'double') };
  if (p.includes('norrtåg')) return { kind: 'regional', electric: true, top: 50, livery: LIVERIES.norrtag, cars: unit(3, 26) };
  if (p.includes('krösatåg')) return { kind: 'regional', electric: pick(2) === 0, top: 44, livery: LIVERIES.krosa, cars: unit(2, 27) };
  if (p.includes('snälltåg')) return { kind: 'intercity', electric: true, top: 44, livery: LIVERIES.snalltaget, cars: [{ shape: 'loco', length: 18 }, ...Array.from({ length: 6 }, () => ({ shape: 'middle' as CarShape, length: 26 }))] };
  if (o.includes('vy') || p.includes('vy')) return { kind: 'intercity', electric: true, top: 44, livery: LIVERIES.vy, cars: [{ shape: 'loco', length: 18 }, ...Array.from({ length: 5 }, () => ({ shape: 'middle' as CarShape, length: 26 }))] };
  if (p.includes('sj regional')) return { kind: 'regional', electric: true, top: 55, livery: LIVERIES.sj, cars: unit(3 + pick(2), 27, 'double') };
  if (p.includes('intercity') || p.includes('sj')) return { kind: 'intercity', electric: true, top: 44, livery: LIVERIES.sj, cars: [{ shape: 'loco', length: 18 }, ...Array.from({ length: 5 + pick(3) }, () => ({ shape: 'middle' as CarShape, length: 26 }))] };
  if (p === '') return freight(seed, o.includes('green') ? LIVERIES.greenCargo : undefined);
  return { kind: 'regional', electric: true, top: 44, livery: LIVERIES.plain, cars: unit(3, 26) };
}

/** A freight train: one or two locomotives and a string of wagons of one kind or a mix. */
function freight(seed: number, livery?: Livery): Consist {
  const wagons = 10 + Math.floor(rand(seed, 2) * 16);
  const kinds: CarShape[] = ['van', 'tank', 'flat'];
  const mixed = rand(seed, 3) < 0.4;
  const one = kinds[Math.floor(rand(seed, 4) * 3)];
  const locos = rand(seed, 5) < 0.3 ? 2 : 1;
  const cars: CarSpec[] = [
    ...Array.from({ length: locos }, (): CarSpec => ({ shape: 'loco', length: 19 })),
    ...Array.from({ length: wagons }, (_, i): CarSpec => {
      const shape = mixed ? kinds[Math.floor(rand(seed, 10 + i) * 3)] : one;
      return { shape, length: shape === 'flat' ? 20 : shape === 'tank' ? 16 : 18 };
    }),
  ];
  return { kind: 'freight', electric: rand(seed, 6) < 0.8, top: 28, livery: livery ?? (rand(seed, 7) < 0.5 ? LIVERIES.greenCargo : LIVERIES.red), cars };
}

/** A made-up train for anywhere: mostly regional units, some intercity and freight, by `seed`. */
export function madeUpConsist(seed: number): Consist {
  const r = rand(seed, 0);
  if (r < 0.25) return freight(seed);
  if (r < 0.45) return { kind: 'intercity', electric: true, top: 44, livery: rand(seed, 8) < 0.5 ? LIVERIES.plain : LIVERIES.red,
    cars: [{ shape: 'loco', length: 18 }, ...Array.from({ length: 4 + Math.floor(rand(seed, 9) * 4) }, () => ({ shape: 'middle' as CarShape, length: 26 }))] };
  if (r < 0.55) return { kind: 'commuter', electric: true, top: 36, livery: LIVERIES.sl, cars: unit(4 + Math.floor(rand(seed, 9) * 3), 20) };
  const liveries = [LIVERIES.plain, LIVERIES.vy, LIVERIES.norrtag, LIVERIES.krosa, LIVERIES.oresund];
  return { kind: 'regional', electric: rand(seed, 6) < 0.75, top: 40, livery: liveries[Math.floor(rand(seed, 8) * liveries.length)], cars: unit(2 + Math.floor(rand(seed, 9) * 3), 26) };
}

/** A whole train's length over the buffers, metres. */
export function lengthOf(c: Consist): number {
  return c.cars.reduce((sum, car) => sum + car.length, 0);
}

/** A number in 0..1 from `seed` for its `k`th choice, the same each time. */
function rand(seed: number, k: number): number {
  let h = (Math.imul(seed | 0, 0x9e3779b1) ^ Math.imul(k + 1, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
